import React, { useState } from 'react';
import type { IconKind, MenuInputOption } from '@saviynt/design-system';
import {
  ApplicationIcon,
  Button,
  DateAndTimePicker,
  Icon,
  InputField,
  MenuInput,
  SidePanel,
  Typography,
} from '@saviynt/design-system';

import { SliderComponent } from '../../../../../../shared/components';
import { useInvertedLocalize } from '../../../../../../shared/hooks';

import type {
  AddLicensePanelProps,
  LicenseFormData,
  TierAllocation,
  TierKey,
} from './type';

import './AddLicensePanel.css';

const DEFAULT_COST_PER_FUE = 960;
const DEFAULT_TOTAL_FUE = 4200;

const TIER_CONFIG: {
  key: TierKey;
  name: string;
  description: string;
  iconKind: string;
  color: string;
  defaultFue: number;
}[] = [
  {
    key: 'advanced',
    name: 'Advanced',
    description: 'Full configuration & admin',
    iconKind: 'AdvancedTier',
    color: '#F59E0B',
    defaultFue: 522,
  },
  {
    key: 'core',
    name: 'Core',
    description: 'Day-to-day transactional users',
    iconKind: 'User',
    color: '#14B8A6',
    defaultFue: 792,
  },
  {
    key: 'selfService',
    name: 'Self-Service',
    description: 'Read-only / occasional users',
    iconKind: 'Document',
    color: '#8B5CF6',
    defaultFue: 1559,
  },
  {
    key: 'developer',
    name: 'Developer',
    description: 'Sandbox & integration developers',
    iconKind: 'Code',
    color: '#3B82F6',
    defaultFue: 1327,
  },
];

const createDefaultTiers = (): Record<TierKey, TierAllocation> => ({
  advanced: { fueCount: 522, costPerFue: DEFAULT_COST_PER_FUE },
  core: { fueCount: 792, costPerFue: DEFAULT_COST_PER_FUE },
  selfService: { fueCount: 1559, costPerFue: DEFAULT_COST_PER_FUE },
  developer: { fueCount: 1327, costPerFue: DEFAULT_COST_PER_FUE },
});

const INITIAL_FORM = (vendor: string): LicenseFormData => ({
  vendor,
  licenseName: '',
  totalFUE: DEFAULT_TOTAL_FUE,
  costPerFue: DEFAULT_COST_PER_FUE,
  startDate: '',
  endDate: '',
  endpointScope: [],
  notes: '',
  tiers: createDefaultTiers(),
});

function formatNumber(n: number): string {
  return n.toLocaleString('en-US');
}

function formatCurrency(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

/**
 * Side panel form for adding a new SAP license to the system.
 * Allows configuring vendor, license name, total FUE, tier allocation with
 * sliders (capped so allocations cannot exceed total FUE), cost per FUE,
 * contract dates, endpoint scope, and notes.
 *
 * @param {AddLicensePanelProps} props - Component props
 * @param {boolean} props.isOpen - Whether the side panel is visible
 * @param {() => void} props.onClose - Callback invoked when the panel is closed
 * @param {(licenseData: LicenseFormData) => void} [props.onSave] - Callback invoked with form data on save
 * @param {string} [props.vendor='sap'] - Pre-selected vendor identifier shown in the locked vendor field
 * @returns {React.ReactElement} A SidePanel containing the add-license form
 */
function AddLicensePanel({
  isOpen,
  onClose,
  onSave,
  vendor = 'sap',
  endpointOptions = [],
  isSaving = false,
}: AddLicensePanelProps): React.ReactElement {
  const localize = useInvertedLocalize();

  const [formData, setFormData] = useState<LicenseFormData>(
    INITIAL_FORM(vendor)
  );
  const [selectedScopeOptions, setSelectedScopeOptions] = useState<
    MenuInputOption[]
  >([]);
  const [touched, setTouched] = useState<{
    licenseName?: boolean;
    startDate?: boolean;
    endDate?: boolean;
  }>({});
  const [isDirty, setIsDirty] = useState(false);
  const [startDateObj, setStartDateObj] = useState<Date | null>(null);
  const [endDateObj, setEndDateObj] = useState<Date | null>(null);

  const endpointScopeOptions: MenuInputOption[] = endpointOptions.map((ep) => ({
    label: ep.label,
    value: ep.value,
  }));

  const allocatedFUE = Object.values(formData.tiers).reduce(
    (sum, t) => sum + t.fueCount,
    0
  );

  const estimatedAnnualValue = formData.totalFUE * formData.costPerFue;

  const setTierFue = (key: TierKey, raw: string): void => {
    setIsDirty(true);
    setFormData((prev) => {
      const total = Math.max(1, prev.totalFUE);
      // Budget available = total minus what other tiers are using
      const otherTiersTotal = Object.entries(prev.tiers).reduce(
        (sum, [k, t]) => (k === key ? sum : sum + t.fueCount),
        0
      );
      const maxForThisTier = Math.max(0, total - otherTiersTotal);
      const value = Math.max(
        0,
        Math.min(maxForThisTier, parseInt(raw, 10) || 0)
      );

      return {
        ...prev,
        tiers: {
          ...prev.tiers,
          [key]: { ...prev.tiers[key], fueCount: value },
        },
      };
    });
  };

  const handleSliderChange = (key: TierKey, pct: number): void => {
    setIsDirty(true);
    setFormData((prev) => {
      const total = Math.max(1, prev.totalFUE);
      // Budget available = total minus what other tiers are using
      const otherTiersTotal = Object.entries(prev.tiers).reduce(
        (sum, [k, t]) => (k === key ? sum : sum + t.fueCount),
        0
      );
      const maxForThisTier = Math.max(0, total - otherTiersTotal);
      const rawValue = Math.round((pct / 100) * total);
      const value = Math.min(maxForThisTier, rawValue);

      return {
        ...prev,
        tiers: {
          ...prev.tiers,
          [key]: { ...prev.tiers[key], fueCount: value },
        },
      };
    });
  };

  const setTotalFUE = (raw: string): void => {
    setIsDirty(true);
    const value = Math.max(0, parseInt(raw, 10) || 0);

    setFormData((prev) => ({ ...prev, totalFUE: value }));
  };

  const setCostPerFue = (raw: string): void => {
    setIsDirty(true);
    const value = Math.max(0, parseInt(raw, 10) || 0);

    setFormData((prev) => ({ ...prev, costPerFue: value }));
  };

  const setField =
    (field: 'licenseName' | 'startDate' | 'endDate' | 'notes') =>
    (value: string): void => {
      setIsDirty(true);
      setFormData((prev) => ({ ...prev, [field]: value }));

      if (field !== 'notes') {
        setTouched((prev) => ({ ...prev, [field]: true }));
      }
    };

  const handleSave = (): void => {
    onSave?.(formData);
  };

  const handleClose = (): void => {
    setFormData(INITIAL_FORM(vendor));
    setSelectedScopeOptions([]);
    setTouched({});
    setIsDirty(false);
    setStartDateObj(null);
    setEndDateObj(null);
    onClose();
  };

  // ── Validation ──────────────────────────────────────────────────────────────
  const today = new Date();

  today.setHours(0, 0, 0, 0);

  const licenseNameInvalid =
    !!formData.licenseName &&
    !/^[a-zA-Z0-9 &_\-–]+$/.test(formData.licenseName);

  const startDateInvalid =
    !!formData.startDate && new Date(formData.startDate) < today;

  const endDateInvalid =
    !!formData.endDate &&
    (new Date(formData.endDate) < today ||
      (!!formData.startDate &&
        new Date(formData.endDate) <= new Date(formData.startDate)));

  const endDateErrorMsg =
    !!formData.endDate && new Date(formData.endDate) < today
      ? 'End date cannot be in the past'
      : 'End date must be after the start date';

  const isSaveDisabled =
    !isDirty ||
    !formData.licenseName ||
    !formData.startDate ||
    !formData.endDate ||
    !formData.totalFUE ||
    !formData.costPerFue ||
    licenseNameInvalid ||
    startDateInvalid ||
    endDateInvalid;

  const buildGradient = (): string => {
    const total = Math.max(1, formData.totalFUE);
    let cumulative = 0;
    const stops: string[] = [];

    TIER_CONFIG.forEach(({ color, key }) => {
      const pct = Math.min(100, (formData.tiers[key].fueCount / total) * 100);

      stops.push(`${color} ${cumulative.toFixed(2)}%`);
      cumulative += pct;
      stops.push(`${color} ${Math.min(100, cumulative).toFixed(2)}%`);
    });

    return `linear-gradient(to right, ${stops.join(', ')})`;
  };

  const vendorLabel = vendor === 'sap' ? 'SAP' : vendor.toUpperCase();

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={handleClose}
      header={{
        title: localize('Add License'),
        description: localize(
          'Capture the SAP contract entitlement and split FUE across tiers.'
        ),
      }}
      layout={
        <div className='LicenseManagement-AddLicensePanel-body'>
          {/* ── Select Vendor (locked) ── */}
          <div className='LicenseManagement-AddLicensePanel-row'>
            <div className='LicenseManagement-AddLicensePanel-field'>
              <Typography
                kind='body2-bold'
                className='LicenseManagement-AddLicensePanel-label'>
                {localize('Select Vendor')}
              </Typography>
              <div className='LicenseManagement-AddLicensePanel-vendorDisplay'>
                <ApplicationIcon
                  logo={vendorLabel}
                  size='xSmall'
                  className='LicenseManagement-AddLicensePanel-vendorIcon'
                />
                <span className='LicenseManagement-AddLicensePanel-vendorText'>
                  {vendorLabel}
                  <span className='LicenseManagement-AddLicensePanel-vendorSub'>
                    {' · ERP - Enterprise Suite'}
                  </span>
                </span>
                <Icon
                  kind='ChevronDown'
                  size='small'
                  className='LicenseManagement-AddLicensePanel-vendorChevron'
                />
              </div>
            </div>
          </div>

          {/* ── Applications (Endpoint Scope) ── */}
          <div className='LicenseManagement-AddLicensePanel-row'>
            <div className='LicenseManagement-AddLicensePanel-field'>
              <MenuInput
                options={endpointScopeOptions}
                selectedOptions={selectedScopeOptions}
                setSelectedOptions={setSelectedScopeOptions}
                onChange={(opts) => {
                  setIsDirty(true);
                  let arr: MenuInputOption[] = [];

                  if (Array.isArray(opts)) {
                    arr = opts as MenuInputOption[];
                  } else if (opts) {
                    arr = [opts as unknown as MenuInputOption];
                  }

                  setFormData((prev) => ({
                    ...prev,
                    endpointScope: arr.map((o: MenuInputOption) => o.value),
                  }));
                }}
                inputLabel={
                  <Typography kind='body2-bold'>
                    {localize('Applications')}
                  </Typography>
                }
                placeholder={localize(
                  'Select endpoints scope covered by this license'
                )}
                isMulti
              />
            </div>
          </div>

          {/* ── License Name ── */}
          <div className='LicenseManagement-AddLicensePanel-row'>
            <div className='LicenseManagement-AddLicensePanel-field'>
              <InputField
                name='licenseName'
                placeholder={localize('e.g. SAP RISE FUE 2026')}
                value={formData.licenseName}
                setValue={setField('licenseName')}
                label={
                  <Typography kind='body2-bold'>
                    {localize('License name')}
                  </Typography>
                }
              />
              {touched.licenseName && licenseNameInvalid && (
                <span className='LicenseManagement-AddLicensePanel-fieldError'>
                  {localize(
                    'Only letters, numbers, spaces, &, dashes and underscores are allowed'
                  )}
                </span>
              )}
            </div>
          </div>

          {/* ── Contract Dates ── */}
          <div className='LicenseManagement-AddLicensePanel-row LicenseManagement-AddLicensePanel-row--double'>
            <div className='LicenseManagement-AddLicensePanel-field'>
              <Typography kind='body2-bold'>
                {localize('Contract Start Date')}
              </Typography>
              <DateAndTimePicker
                kind='dateStart'
                label=''
                startDate={startDateObj}
                setStartDate={(date: Date) => {
                  setIsDirty(true);
                  setStartDateObj(date);
                  setTouched((prev) => ({ ...prev, startDate: true }));
                  setFormData((prev) => ({
                    ...prev,
                    startDate: date.toISOString().split('T')[0],
                  }));
                }}
                endDate={endDateObj}
                setEndDate={setEndDateObj}
                minDate={new Date()}
                maxDate={new Date(2099, 11, 31)}
                portalId='root'
                classNames={{
                  containerClassName:
                    'LicenseManagement-AddLicensePanel-datePickerDark',
                }}
              />
              {touched.startDate && startDateInvalid && (
                <span className='LicenseManagement-AddLicensePanel-fieldError'>
                  {localize('Start date cannot be in the past')}
                </span>
              )}
            </div>
            <div className='LicenseManagement-AddLicensePanel-field'>
              <Typography kind='body2-bold'>
                {localize('Contract End Date')}
              </Typography>
              <DateAndTimePicker
                kind='dateEnd'
                label=''
                startDate={startDateObj}
                setStartDate={setStartDateObj}
                endDate={endDateObj}
                setEndDate={(date: Date) => {
                  setIsDirty(true);
                  setEndDateObj(date);
                  setTouched((prev) => ({ ...prev, endDate: true }));
                  setFormData((prev) => ({
                    ...prev,
                    endDate: date.toISOString().split('T')[0],
                  }));
                }}
                minDate={startDateObj ?? new Date()}
                maxDate={new Date(2099, 11, 31)}
                portalId='root'
                classNames={{
                  containerClassName:
                    'LicenseManagement-AddLicensePanel-datePickerDark',
                }}
              />
              {touched.endDate && endDateInvalid && (
                <span className='LicenseManagement-AddLicensePanel-fieldError'>
                  {localize(endDateErrorMsg)}
                </span>
              )}
            </div>
          </div>

          {/* ── Total FUE + Cost per FUE + Estimated Annual Value ── */}
          <div className='LicenseManagement-AddLicensePanel-row LicenseManagement-AddLicensePanel-row--triple'>
            <div className='LicenseManagement-AddLicensePanel-field'>
              <InputField
                name='totalFUE'
                type='number'
                placeholder='4200'
                value={String(formData.totalFUE || '')}
                setValue={setTotalFUE}
                label={
                  <Typography kind='body2-bold'>
                    {localize('Total FUE in contract')}
                  </Typography>
                }
              />
            </div>
            <div className='LicenseManagement-AddLicensePanel-field'>
              <InputField
                name='costPerFUE'
                type='number'
                placeholder='960'
                value={String(formData.costPerFue || '')}
                setValue={setCostPerFue}
                label={
                  <Typography kind='body2-bold'>
                    {localize('Cost per FUE')}
                  </Typography>
                }
              />
            </div>
            <div className='LicenseManagement-AddLicensePanel-estimatedValue'>
              <span className='LicenseManagement-AddLicensePanel-estimatedLabel'>
                {localize('ESTIMATED ANNUAL VALUE')}
              </span>
              <span className='LicenseManagement-AddLicensePanel-estimatedAmount'>
                {formatCurrency(estimatedAnnualValue)}
              </span>
            </div>
          </div>

          {/* ── TIER ALLOCATION box ── */}
          <div className='LicenseManagement-AddLicensePanel-tierAllocBox'>
            <div className='LicenseManagement-AddLicensePanel-tierAllocHeader'>
              <span className='LicenseManagement-AddLicensePanel-tierAllocTitle'>
                {localize('TIER ALLOCATION')}
              </span>
              <span className='LicenseManagement-AddLicensePanel-tierAllocCounter'>
                {formatNumber(allocatedFUE)} {localize('/')}{' '}
                {formatNumber(formData.totalFUE)} {localize('FUE')}{' '}
                {localize('(')}
                {localize('Remaining')}{' '}
                {formatNumber(Math.max(0, formData.totalFUE - allocatedFUE))}
                {localize(')')}
              </span>
            </div>

            {/* ── Gradient overview bar ── */}
            <div
              className='LicenseManagement-AddLicensePanel-gradientBar'
              style={{ background: buildGradient() }}
              aria-hidden='true'
            />

            {/* ── Tier rows ── */}
            {TIER_CONFIG.map(
              ({ key, name, description, iconKind, color }, idx) => {
                const tier = formData.tiers[key];
                const total = Math.max(1, formData.totalFUE);
                const pct = Math.round((tier.fueCount / total) * 100);
                const pctDisplay = `${pct}%`;
                const sliderPct = Math.min(100, (tier.fueCount / total) * 100);
                const isLast = idx === TIER_CONFIG.length - 1;

                return (
                  <div
                    key={key}
                    className={`LicenseManagement-AddLicensePanel-tierRow${
                      isLast
                        ? ' LicenseManagement-AddLicensePanel-tierRow--last'
                        : ''
                    }`}>
                    {/* Icon + name + desc | count + % */}
                    <div className='LicenseManagement-AddLicensePanel-tierHeader'>
                      <div className='LicenseManagement-AddLicensePanel-tierInfo'>
                        <span
                          className='LicenseManagement-AddLicensePanel-tierIcon'
                          style={{ color }}>
                          <Icon
                            kind={iconKind as IconKind}
                            size='small'
                            color={color}
                          />
                        </span>
                        <div className='LicenseManagement-AddLicensePanel-tierText'>
                          <Typography kind='body2-bold'>
                            {localize(name)}
                          </Typography>
                          <span className='LicenseManagement-AddLicensePanel-tierDesc'>
                            {localize(description)}
                          </span>
                        </div>
                      </div>
                      <div className='LicenseManagement-AddLicensePanel-tierCountWrapper'>
                        <input
                          type='number'
                          className='LicenseManagement-AddLicensePanel-tierCountInput'
                          value={tier.fueCount}
                          onChange={(e) => setTierFue(key, e.target.value)}
                          min={0}
                          max={formData.totalFUE}
                          aria-label={`${name} FUE count`}
                        />
                        <span className='LicenseManagement-AddLicensePanel-tierPct'>
                          {pctDisplay}
                        </span>
                      </div>
                    </div>

                    {/* Slider */}
                    <SliderComponent
                      value={sliderPct}
                      onChange={(val) => handleSliderChange(key, val)}
                      progressColor={color}
                      trackColor='rgba(255,255,255,0.08)'
                      trackHeight='0.375rem'
                      ariaLabel={`${name} allocation`}
                    />
                  </div>
                );
              }
            )}
          </div>

          {/* ── Notes ── */}
          <div className='LicenseManagement-AddLicensePanel-row'>
            <div className='LicenseManagement-AddLicensePanel-field'>
              <InputField
                name='notes'
                kind='multiline'
                placeholder={localize('Contract reference, owner, etc.')}
                value={formData.notes}
                setValue={setField('notes')}
                label={
                  <Typography kind='body2-bold'>
                    {localize('Notes (optional)')}
                  </Typography>
                }
                rows={4}
              />
            </div>
          </div>
        </div>
      }
      footer={
        <div className='LicenseManagement-AddLicensePanel-footer'>
          <Button
            kind='ghost'
            onClick={handleClose}
            className='LicenseManagement-AddLicensePanel-cancelBtn'>
            {localize('Cancel')}
          </Button>
          <Button
            kind='filled'
            onClick={handleSave}
            isDisabled={isSaveDisabled || isSaving}
            className='LicenseManagement-AddLicensePanel-saveBtn'>
            {localize('Save license')}
          </Button>
        </div>
      }
      className='LicenseManagement-AddLicensePanel-sidePanel'
    />
  );
}

export default AddLicensePanel;
